import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'
import {
  DateFieldState,
  DatePickerState,
  DateRangePickerState,
  DateSegment,
  useDateFieldState,
} from 'react-stately'
import {
  AriaDatePickerProps,
  useDateField,
  useDateSegment,
  useLocale,
  mergeProps,
} from 'react-aria'
import { DateValue } from '@internationalized/date'
import { Box, Flex, Text } from '../../../core-components'
import { screenReaderOnlyCss } from '../../../core-components/screen-reader-only/ScreenReaderOnly'
import { Theme } from '../../../theme'
import { createCalendar } from './BaseCalendar'

type Props = AriaDatePickerProps<DateValue> & {
  datePickerState: DatePickerState | DateRangePickerState
  onSegmentToFocusChange: (segment: HTMLElement) => void
  placeholder?: string
  popoverRef: React.RefObject<HTMLDivElement | null>
  size?: 'regular' | 'large'
}
export const DateField = ({
  datePickerState,
  onSegmentToFocusChange,
  popoverRef,
  placeholder,
  size = 'regular',
  ...props
}: Props) => {
  const { locale } = useLocale()
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  })
  const ref = React.useRef<HTMLDivElement>(null)
  const { fieldProps } = useDateField(props, state, ref)

  const firstEmptySegmentIndex = state.segments.findIndex(s => s.isPlaceholder)
  const firstEmptySegment =
    firstEmptySegmentIndex === -1
      ? undefined
      : (ref.current?.children[firstEmptySegmentIndex] as HTMLElement)
  useEffect(() => {
    if (firstEmptySegment) {
      onSegmentToFocusChange(firstEmptySegment)
    } else {
      onSegmentToFocusChange(ref.current?.children[0] as HTMLElement)
    }
  }, [firstEmptySegment, onSegmentToFocusChange])

  const guardedOpen = () => {
    if (!props.isDisabled) {
      datePickerState.open()
    }
  }

  const isPlaceholderVisible =
    !!placeholder && state.segments.every(s => s.isPlaceholder || s.type === 'literal')

  return (
    <div
      onClickCapture={e => {
        guardedOpen()
        if (!props.isDisabled && 'focus' in e.target && typeof e.target.focus === 'function') {
          e.target.focus()
        }
      }}
      onFocus={e => {
        if (e.relatedTarget) {
          guardedOpen()
        }
      }}
      onBlur={e => {
        if (e.relatedTarget && !popoverRef.current?.contains(e.relatedTarget)) {
          datePickerState.close()
        }
      }}
    >
      <StyledFlexContainer>
        {isPlaceholderVisible ? (
          <StyledPlaceholderText
            textStyle={size === 'large' ? 'form-value-l' : 'form-value'}
            color='fg/minor'
            aria-hidden
          >
            {placeholder}
          </StyledPlaceholderText>
        ) : null}
        <StyledValueBox {...fieldProps} ref={ref} data-always-visible={!isPlaceholderVisible}>
          {state.segments.map((segment, i) => (
            <DateSegmentComponent
              key={i}
              segment={segment}
              state={state}
              onClose={datePickerState.close}
              size={size}
            />
          ))}
        </StyledValueBox>
      </StyledFlexContainer>
    </div>
  )
}

const StyledFlexContainer = styled(Flex)``
// This doesn't work in storybook because of this issue https://github.com/chromaui/storybook-addon-pseudo-states/issues/31
const StyledValueBox = styled(Box)`
  ${StyledFlexContainer}:not(:focus-within) &:not([data-always-visible='true']) {
    ${screenReaderOnlyCss}
  }
`
const StyledPlaceholderText = styled(Text)`
  ${StyledFlexContainer}:focus-within & {
    display: none;
  }
`

interface SegmentProps {
  onClose: () => void
  segment: DateSegment
  state: DateFieldState
  variant?: 'regular' | 'nested' | 'ghost'
  size?: 'regular' | 'large'
}

export const DateSegmentComponent = ({
  segment,
  state,
  onClose,
  variant,
  size = 'regular',
}: SegmentProps) => {
  const ref = React.useRef(null)
  const { segmentProps } = useDateSegment(segment, state, ref)
  return (
    <StyledText
      {...mergeProps(segmentProps, {
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            e.stopPropagation()
            onClose()
          }
        },
      })}
      ref={ref}
      textStyle={size === 'large' ? 'form-value-l' : 'form-value'}
      mx={segment.type === 'literal' ? 1 : 0}
      color={
        segment.isPlaceholder || segment.type === 'literal'
          ? 'fg/minor'
          : variant === 'ghost'
            ? 'fg/moderate'
            : 'fg/major'
      }
      textTransform='uppercase'
    >
      {segment.text}
    </StyledText>
  )
}
const StyledText = styled(Text)`
  padding: 3px 0;

  :focus {
    outline: none;
    background: ${({ theme }) => theme.colors['bg/form-item-selection']};
  }

  &[aria-disabled='true'] {
    color: ${({ theme }) => theme.colors['fg/disabled']};
  }
`

const wrapperSizes = {
  regular: 8,
  nested: 9,
  ghost: 7,
} as const

export const DateFieldWrapper = styled.div<{
  borderRadius?: 8 | 10
  hasError: boolean
  px?: keyof Theme['space']
  variant: 'regular' | 'nested' | 'ghost'
}>`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  cursor: text;
  border-radius: ${({ theme, borderRadius }) => theme.radii[borderRadius ?? 'none']};
  background: ${({ theme, variant }) =>
    variant === 'ghost' ? 'transparent' : theme.colors['bg/form-item-default']};
  height: ${({ theme, variant }) => theme.sizes[wrapperSizes[variant]]};
  padding-left: ${({ theme, px }) => theme.space[px ?? 5]};
  padding-right: ${({ theme, px }) => theme.space[px ?? 3]};

  :hover {
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
  }

  ${({ hasError, theme }) =>
    hasError &&
    css`
      background: ${theme.colors['bg/form-item-error']};
    `}
  :focus-within,
  &[data-open='true'] {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};
    box-shadow: ${({ theme }) => theme.shadows['border/form-item-nested-focused']};
  }

  &[aria-disabled='true'] {
    cursor: default;
    background: ${({ theme }) => theme.colors['bg/disabled']};
  }
`
